import { property, supertypeClass } from '@haventech/amorphic';
import { Application, ApplicationSourceType } from '../../../common/js/customer/Application';
import { BaseWaiverFlatExtras, Policy } from '../../../common/js/customer/Policy';
import { CoverpathBasePolicy } from './CoverpathBasePolicy';
import _ from 'lodash';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { PremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { ApplicationTypes } from '../../js/interfaces/CoverpathTypes';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export abstract class CoverpathBaseApplication extends Application {
  @property({ type: Array, getType: () => CoverpathBasePolicy })
  policies: Array<CoverpathBasePolicy> = [];

  @property({
    comment: 'Has agent submitted this application',
  })
  isAgentSubmittedApp: boolean = false;

  @property({ comment: 'Application is a tele CMI application' })
  isTeleCMI: boolean = false;

  @property({ comment: 'Application is a tele CMI application by opt-in feature' })
  isTeleCMIOptedIn: boolean = false;

  @property({
    rule: ['required'],
    comment: 'Indicates whether a Tele CMI was completed',
  })
  isTeleCMICompleted: boolean = false;

  @property({ comment: 'Indicates whether the questions are locked by Advisor' })
  lockedByAdvisor: boolean = false;

  init(customer) {
    super.init(customer);
    return this;
  }

  addPolicy(policy: CoverpathBasePolicy) {
    this.policies.push(policy);

    /* This is needed to ensure all the child objects are synced.
        This only happens with a setter, so it wasn't needed when we were setting applicationPolicy.
        Now we just push the policy into an array which doesn't do the same thing so objects like beneficiary
        are being created but not available on the client side until refresh. */
    this.policies = this.policies;
  }

  addSecondaryPolicy(policy: CoverpathBasePolicy) {
    this.policies.forEach((pol) => {
      if (pol.isSecondary) {
        pol.type = '';
      }
    });
    policy.type = POLICY_TYPES.SECONDARY;
    this.policies.push(policy);

    /* This is needed to ensure all the child objects are synced.
        This only happens with a setter, so it wasn't needed when we were setting applicationPolicy.
        Now we just push the policy into an array which doesn't do the same thing so objects like beneficiary
        are being created but not available on the client side until refresh. */
    this.policies = this.policies;
  }

  /**
   *
   * How it's different from cloneApplication (use cases, etc. Can link jira ticket too if you want)
   * This is the same as cloneApplication except we are letting amorphic save the change during postServer call, i.e.
   * - no transient changes
   * - no intermediate transaction
   *
   * @see {@link https://massmutual.atlassian.net/browse/HL-19990|cancel-reapply-issue}
   *
   * Design
   [OUTSIDE] START preServer call should the data refresh
   1. Clone the policies
   2. Clone the app => 2 active apps
   3. Fix [new policy]-to-[new application] reference
   4. Mark old application as prior
   5. Add new application to the applications array
   [OUTSIDE] END postServer call should do the saving
   */
  async cloneApplicationInSession(
    sourceType: ApplicationSourceType,
    newProgress?: number
  ): Promise<CoverpathBaseApplication> {
    const oldApplication = this;
    const customer = this.customer;

    // step 1, 2, 3
    const newApplication = await this.createClone(sourceType);
    await newApplication.assignApplicationNumber();

    // step 4 - Mark old application as prior
    oldApplication.type = ApplicationTypes.PRIOR;
    customer.priorApplications.push(oldApplication);

    // step 5 - set application source for tracing
    newApplication.setSource(sourceType, oldApplication.applicationNumber);

    // step 6 - Add new application to the applications array
    // @NOTE this should actually be this.customer.applications.push(newApplication); However, the "application setter" is doing that work
    customer.application = newApplication;

    //step 7 record original application number
    newApplication.originalApplicationNumber = oldApplication.originalApplicationNumber
      ? oldApplication.originalApplicationNumber
      : oldApplication.applicationNumber;

    // START [OLD CODE] not sure why this is here
    customer.primaryCustomer = newApplication.primaryPolicy.insured;
    if (newApplication.secondaryPolicy) {
      newApplication.secondaryPolicy.insured = newApplication.primaryPolicy.insured;
    }

    if (typeof newProgress !== 'undefined') {
      customer.resetApplicationProgress(newProgress);
    }
    // END [OLD CODE] not sure why this is here
    return newApplication;
  }

  /**
   * Same as createClonedApplicationAsync, except
   * - We don't want to make the fetch call because it overides non-peristed changes, so we call "createClonedPolicy" instead of "fetchAndCreateClonedPolicy"
   * - We are not marking oldApplication as prior
   * @see {@link https://massmutual.atlassian.net/browse/HL-19990|cancel-reapply-issue}
   */
  async createClone(sourceType?: ApplicationSourceType): Promise<CoverpathBaseApplication> {
    // step 1
    let filteredPolicies: CoverpathBasePolicy[];

    if (sourceType === ApplicationSourceType.TERM_CONVERSION) {
      filteredPolicies = this.policies.filter((policy) => policy.type === POLICY_TYPES.PRIMARY);
    } else {
      filteredPolicies = this.policies.filter(
        (policy) => policy.type === POLICY_TYPES.PRIMARY || policy.type === POLICY_TYPES.SECONDARY
      );
    }

    const clonedPolicies = await Promise.all<CoverpathBasePolicy>(
      filteredPolicies.map<Promise<CoverpathBasePolicy>>(
        (policy) => policy.createClonedPolicy(undefined, sourceType) as Promise<CoverpathBasePolicy>
      )
    );

    // STEP 2 - Clone the app => 2 active apps
    let newApp = this.getApplicationType().new(this.customer);

    // STEP 3 - Fix [new policy]-to-[new application] reference
    await Promise.all(
      clonedPolicies.map(async (policy) => {
        await this.clonePremiumBreakdown(policy);
        policy.application = newApp;
      })
    );
    newApp.policies = clonedPolicies;

    // START [OLD CODE] not sure why this is here
    const isAppSubmitted = !!this.primaryPolicy.workflowState;
    // copy over relevant application properties based on which flavor of application this is.
    newApp = await newApp.cloneApplicationProps(this, isAppSubmitted);
    newApp.examVendor = this.examVendor;

    this.updatePrimaryCustomerData(this, newApp);
    this.updateQuote(this, newApp);
    // END [OLD CODE] not sure why this is here

    return newApp as CoverpathBaseApplication;
  }

  protected async clonePremiumBreakdown(policy: Policy) {
    const breakdown = policy.premiumBreakdown;

    if (!breakdown || breakdown.base === 0 || breakdown.base == null) {
      //if policy does not have a premium, calculate premium for cloned policy.
      let premiumResult: BaseWaiverFlatExtras;
      try {
        const rateClass = RateClasses[this.quote.healthClass].code;
        premiumResult = await policy.fetchBaseWaiverFlatExtras(rateClass);
      } catch (err) {
        UILoggerSingleton.getInstance().error(
          {
            module: 'CoverpathBaseApplication',
            function: 'clonePremiumBreakdown',
            category: UICategory.request,
            error: err,
          },
          'Could not fetch premium'
        );
        return;
      }
      //create or update premium breakdown
      if (!breakdown) {
        const premiumBreakdown = PremiumBreakdown.new(
          premiumResult.base,
          premiumResult.waiver,
          premiumResult.flatExtras,
          policy
        );
        policy.premiumBreakdown = premiumBreakdown;
      } else {
        policy._updatePremiumBreakdown(premiumResult);
      }
      policy._updateRiders(premiumResult.riders);
    }
    policy.productPremiumVersion = null;
  }

  protected updateQuote(oldApplication: CoverpathBaseApplication, newApplication: Application): void {
    if (oldApplication.quote) {
      // update quote to point to the new application (this)
      oldApplication.quote.application = newApplication;

      // since the quote now points to the new application, dis-associate it from the old (prior) application.
      oldApplication.quote = null;
    }
  }

  setSource(sourceType: ApplicationSourceType, sourceAppNumber?: string): void {
    this.sourceType = sourceType;
    this.sourceAppNumber = sourceAppNumber;
  }

  //Overriden in CasApplication
  isDraftApplication(hasRegistered: boolean) {
    return false;
  }

  hasAnyInForce(): boolean {
    return this.primaryPolicy.isInForce();
  }

  hasOnlyCanceled(): boolean {
    return this.primaryPolicy.hasEnded();
  }

  getTotalPremium(): number {
    return this.primaryPolicy.premiumBreakdown.getTotal();
  }

  getTotalTlicPremium(): number {
    let amount = 0;
    if (this.primaryPolicy.tLICPremiumBreakdown) {
      amount += this.primaryPolicy.tLICPremiumBreakdown.getTotal();
    }

    return amount;
  }

  isTeleCMITrigger() {
    this.isTeleCMIOptedIn = this.isTeleCMI;
  }
}
